/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React, { useMemo } from 'react';
import Scrollbars from 'react-custom-scrollbars-2';
import styled, { css } from 'styled-components';
import Config from '../../../../shared/entities/config';
import { NetworkGroup, SatelliteInFov, TerminalDetails, TerminalTypeLabel } from '../../../../shared/entities/terminal';
import { DetailsPanelGroupCollapseState } from '../../../../store/ui/reducer';
import { isOnline } from '../../../../utils/ConnectionStatusUtils';
import { getOtherTerminalOnAircraft } from '../../../../utils/TerminalUtilities';
import { PANEL_HEADER_HEIGHT_PX, TAIL_DETAILS_PANEL_WIDTH } from '../TailDetailsPanel';
import OfflineTerminalDetailsLastReportedBanner from './OfflineTerminalDetailsLastReportedBanner';
import { PANEL_TAB_HEIGHT_PX } from './TerminalTabContainer';
import TerminalTabDataDefault from './TerminalTabDataDefault';
import TerminalTabDataLinkway from './TerminalTabDataLinkway';
import { DetailsPanelEnum } from '../../../../shared/entities/detailsPanel';
import TerminalTabDataRTE from './TerminalTabDataRTE';
import TerminalTabDataGX from './TerminalTabDataGX';

interface TerminalTabPanelProps extends TerminalTabPanelStateProps, TerminalTabPanelDispatchProps {}

export interface TerminalTabPanelStateProps {
  detailsPanelVariant: DetailsPanelEnum;
  hidden: boolean;
  satellitesInFOVIndexOffset?: number;
  tailDetailsPanelTerminal?: TerminalDetails;
  detailsPanelGroupCollapseState: DetailsPanelGroupCollapseState;
  isKuPlusKa: boolean;
  isDualTerminal: boolean;
  isOverviewMode: boolean;
  isFullTailViewMode: boolean;
  isNextSatellitePanelEnabled: boolean;
  isSatellitesInFOVEnabled: boolean;
  appConfig: Config;
  history: any;
  showDetailedConnectionStats: boolean;
}

export interface TerminalTabPanelDispatchProps {
  setDetailsPanelGroupCollapseState: (detailsPanelGroupCollapseState: DetailsPanelGroupCollapseState) => {};
}

const OVERVIEW_MODE_OFFSET_PX = 100;
const SCROLLBAR_HEIGHT_OFFSET_PX = 134;
const SCROLLBAR_WRAPPER_HEIGHT_OFFSET_PX = 204;
const FLEET_VIEW_PANEL_WIDTH_ADJUSTMENT_PX = 2;
export const OFFLINE_BANNER_HEIGHT_PX = 42;

export interface ScrollbarProps {
  scrollbarHeightOffset: number;
}

const StyledScrollbarWrapper = styled.div`
  ${({ scrollbarHeightOffset }: ScrollbarProps) => css`
    height: ${`calc(100vh - ${scrollbarHeightOffset}px)`};
  `};
`;

const calculateScrollbarWrapperHeightOffset = (
  isOffline: boolean,
  isFullTailViewMode: boolean,
  isMultiAircraftTerminal: boolean,
  detailsPanelVariant: DetailsPanelEnum,
) => {
  let offset = SCROLLBAR_WRAPPER_HEIGHT_OFFSET_PX;
  if (isOffline) {
    offset += OFFLINE_BANNER_HEIGHT_PX;
  }
  if (isFullTailViewMode && detailsPanelVariant !== DetailsPanelEnum.SPACE_VEHICLE) {
    offset -= PANEL_HEADER_HEIGHT_PX;
  }
  if (!isMultiAircraftTerminal) {
    offset -= PANEL_TAB_HEIGHT_PX;
  }
  return offset;
};

export const calculateScrollbarHeightOffset = (
  tailDetailsPanelTerminal: TerminalDetails | undefined,
  isOverviewMode: boolean = false,
) => {
  const isPanelOffline = !isOnline((tailDetailsPanelTerminal || {}).connectivityStatus);
  let offset = SCROLLBAR_HEIGHT_OFFSET_PX;
  if (tailDetailsPanelTerminal) {
    if (!isPanelOffline) {
      offset += OFFLINE_BANNER_HEIGHT_PX;
    }
    if (isOverviewMode) {
      offset += OVERVIEW_MODE_OFFSET_PX;
    }
  }
  return offset;
};

export const CollapseGroupScrollbarContainer = styled.div`
  ${({ scrollbarHeightOffset }: ScrollbarProps) => css`
    height: ${`calc(100% - ${scrollbarHeightOffset}px)}`};
  `};
  min-height: 100px;
  position: absolute;
  width: 94%;
  box-sizing: border-box;
`;

const TerminalTabPanel = (props: TerminalTabPanelProps) => {
  const {
    detailsPanelVariant,
    hidden,
    tailDetailsPanelTerminal,
    isKuPlusKa,
    isDualTerminal,
    isOverviewMode,
    isFullTailViewMode,
  } = props;

  let isOffline = isOverviewMode
    ? !isOnline((tailDetailsPanelTerminal || {}).connectivityStatus) &&
      !isOnline(getOtherTerminalOnAircraft(tailDetailsPanelTerminal).connectivityStatus)
    : !isOnline((tailDetailsPanelTerminal || {}).connectivityStatus);

  const isMultiTerminalAircraft = isDualTerminal || isKuPlusKa;

  let scrollbarWrapperHeightOffset = SCROLLBAR_WRAPPER_HEIGHT_OFFSET_PX;
  if (tailDetailsPanelTerminal) {
    scrollbarWrapperHeightOffset = calculateScrollbarWrapperHeightOffset(
      isOffline,
      isFullTailViewMode,
      isMultiTerminalAircraft,
      detailsPanelVariant,
    );
  }
  const terminalTabData = useMemo(() => {
    if (
      detailsPanelVariant === DetailsPanelEnum.SPACE_VEHICLE &&
      tailDetailsPanelTerminal?.terminalTypeLabel === TerminalTypeLabel.VI
    ) {
      return <TerminalTabDataDefault {...props} />;
    } else if (
      detailsPanelVariant === DetailsPanelEnum.SPACE_VEHICLE &&
      tailDetailsPanelTerminal?.terminalTypeLabel === TerminalTypeLabel.RTE
    ) {
      return <TerminalTabDataRTE {...props} />;
    } else if (tailDetailsPanelTerminal?.networkGroup === NetworkGroup.LINKWAY) {
      return <TerminalTabDataLinkway {...props} />;
    } else if (tailDetailsPanelTerminal?.networkGroup === NetworkGroup.GX) {
      return <TerminalTabDataGX {...props} />;
    } else {
      return <TerminalTabDataDefault {...props} />;
    }
  }, [detailsPanelVariant, tailDetailsPanelTerminal, props]);

  return !hidden ? (
    <React.Fragment>
      {tailDetailsPanelTerminal && isOffline ? (
        <OfflineTerminalDetailsLastReportedBanner tailDetailsPanelTerminal={tailDetailsPanelTerminal} />
      ) : null}

      <StyledScrollbarWrapper scrollbarHeightOffset={scrollbarWrapperHeightOffset}>
        <Scrollbars
          style={{
            width: `${TAIL_DETAILS_PANEL_WIDTH - (!isFullTailViewMode ? FLEET_VIEW_PANEL_WIDTH_ADJUSTMENT_PX : 0)}px`,
          }}
          className="app__scrollbar"
        >
          {terminalTabData}
        </Scrollbars>
      </StyledScrollbarWrapper>
    </React.Fragment>
  ) : null;
};

export interface HighlightedSatelliteInFov {
  satelliteName: string;
  sticky: boolean;
  setByGraphicIndicator: boolean;
  setBySatelliteList: boolean;
}

export interface SatelliteDisplayProps {
  satelliteInFov: SatelliteInFov;
  index: number;
  isHighlighted: boolean;
  isStickyMode: boolean | undefined;
  setHighlightedSatelliteInFov: Function;
}

export default TerminalTabPanel;
